import { useContext } from "react";
import styled from "styled-components";
import css from "@styled-system/css";

import { DatePickerContext } from "./DatePickerContext";
import { DatePickerControlsProps } from "./datePicker.type";

import { monthNames } from "./enums";

import Box from "../Box/Box";
import Button from "../Button/Button";
import moment from "moment";
import Icon from "../Icon/Icon";

const DatePickerControlsStyled = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 26px;
  ${css({
    fontSize: 1,
    marginBottom: 6,
  })}
`;

const DatePickerNav = styled(Button)`
  height: auto;
  padding: 0;

  * {
    margin: 0;
  }

  ${css({
    "&:not([disabled]):hover": {
      svg: {
        color: "blue-hover",
      },
    },
  })}
`;

const DatePickerLayoutToggle = styled(Button)`
  ${css({
    color: "primary-navy-blue",
    fontSize: 1,
  })}
`;

const DatePickerControls = ({ minDate, maxDate }: DatePickerControlsProps) => {
  const {
    selectedMonth,
    selectedYear,
    selectedYearsRange,
    calendarLayout,
    incrementSelectedMonth,
    decrementSelectedMonth,
    incrementSelectedYear,
    decrementSelectedYear,
    incrementSelectedYearsRange,
    decrementSelectedYearsRange,
    setCalendarLayout,
  } = useContext(DatePickerContext);

  const changeLayout = () => {
    if (calendarLayout === "days") {
      setCalendarLayout("months");
    } else if (calendarLayout === "months") {
      setCalendarLayout("years");
    }
  };

  const checkIsEnabledPrev = () => {
    if (minDate) {
      switch (calendarLayout) {
        case "years":
          return moment(minDate).year() < selectedYearsRange.startYear;
        case "months":
          return moment(minDate).year() < selectedYear;
        case "days":
          return (
            moment(minDate).month() < selectedMonth ||
            moment(minDate).year() < selectedYear
          );
      }
    }
    return true;
  };

  const checkIsEnabledNext = () => {
    if (maxDate) {
      switch (calendarLayout) {
        case "years":
          return moment(maxDate).year() > selectedYearsRange.endYear;
        case "months":
          return moment(maxDate).year() > selectedYear;
        case "days":
          return (
            moment(maxDate).month() > selectedMonth ||
            moment(maxDate).year() > selectedYear
          );
      }
    }
    return true;
  };

  const isPrevEnabled = checkIsEnabledPrev();
  const isNextEnabled = checkIsEnabledNext();

  return (
    <DatePickerControlsStyled aria-label="datepicker controls">
      <DatePickerLayoutToggle
        variant="secondary-white"
        p={0}
        height="auto"
        onClick={changeLayout}
        aria-label="datepicker layout toggle"
      >
        {
          {
            days: `${monthNames[selectedMonth]} ${selectedYear}`,
            months: selectedYear,
            years: `${selectedYearsRange.startYear} - ${selectedYearsRange.endYear}`,
          }[calendarLayout]
        }
        {calendarLayout !== "years" && (
          <Icon
            name="angle-down"
            weight="solid"
            width={15}
            height={22}
            marginLeft={4}
          ></Icon>
        )}
      </DatePickerLayoutToggle>
      <Box>
        <DatePickerNav
          variant="secondary-white"
          icon="angle-left"
          iconWeight="solid"
          iconWidth={22}
          iconHeight={22}
          iconColor="primary-light-blue"
          marginRight={3}
          onClick={
            {
              days: decrementSelectedMonth,
              months: decrementSelectedYear,
              years: decrementSelectedYearsRange,
            }[calendarLayout]
          }
          disabled={!isPrevEnabled}
          aria-label="datepicker prev"
        />
        <DatePickerNav
          variant="secondary-white"
          icon="angle-right"
          iconWeight="solid"
          iconWidth={22}
          iconHeight={22}
          iconColor="primary-light-blue"
          marginLeft={3}
          onClick={
            {
              days: incrementSelectedMonth,
              months: incrementSelectedYear,
              years: incrementSelectedYearsRange,
            }[calendarLayout]
          }
          disabled={!isNextEnabled}
          aria-label="datepicker next"
        />
      </Box>
    </DatePickerControlsStyled>
  );
};

export default DatePickerControls;
